"use client";

import { useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";

import { FadeIn } from "@/components/global/utils/FadeIn";

import { CountryFlag } from "./CountryFlag";
import { Laurel } from "./Laurel";
import type { LeaderboardEntry } from "./types";

type Rank = 1 | 2 | 3;

const SHAPES = {
  tall: {
    h: 482,
    border:
      "M46.5 0 L0 46.6 L0 382 L133.34 481.24 L267 382 L267 45.71 L220.38 0 Z",
    clip: "M0.17416 0 L0 0.09668 L0 0.79253 L0.4994 0.99842 L1 0.79253 L1 0.09484 L0.82539 0 Z",
    wrap: "M46.2427 0.294067 L0.419849 46.1162 L0.415943 391.965 L30.7743 415.67",
  },
  short: {
    h: 380,
    border:
      "M46.28 0 L0 46.21 L0 280 L133.08 379.15 L267 280 L267 45.96 L220.12 0 Z",
    clip: "M0.17335 0 L0 0.12161 L0 0.73684 L0.49842 0.99776 L1 0.73684 L1 0.12094 L0.82441 0 Z",
    wrap: "M46.25 0.29 L0.42 46.12 L0.42 287.97 L30.77 311.67",
  },
} as const;

type Stop = { o: number; c: string; op?: number };
const THEME: Record<
  Rank,
  { accent: string; shape: keyof typeof SHAPES; stroke: Stop[] }
> = {
  1: {
    accent: "#ffda74",
    shape: "tall",
    stroke: [
      { o: 0, c: "#fbf3cf" },
      { o: 0.06, c: "#e6cd88" },
      { o: 0.2, c: "#bd9a56" },
      { o: 0.4, c: "#7a5730" },
      { o: 0.68, c: "#5c371a", op: 0 },
    ],
  },
  2: {
    accent: "#d2d2d2",
    shape: "short",
    stroke: [
      { o: 0, c: "#fbfcfc" },
      { o: 0.07, c: "#c6d3db" },
      { o: 0.35, c: "#abbdc8" },
      { o: 0.55, c: "#828e96" },
      { o: 0.68, c: "#5a636a", op: 0 },
    ],
  },
  3: {
    accent: "#e3b991",
    shape: "short",
    stroke: [
      { o: 0, c: "#c8b4a0" },
      { o: 0.07, c: "#927e6d" },
      { o: 0.28, c: "#6e5a4b" },
      { o: 0.5, c: "#59483d" },
      { o: 0.68, c: "#3a2f26", op: 0 },
    ],
  },
};

const LABELS: Record<Rank, string> = { 1: "1st", 2: "2nd", 3: "3rd" };

function PointsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 21 21" fill="none" aria-hidden className={className}>
      <path
        d="M9 0.866025C9.9282 0.330127 11.0718 0.330127 12 0.866026L18.0933 4.38397C19.0215 4.91987 19.5933 5.91025 19.5933 6.98205V14.0179C19.5933 15.0897 19.0215 16.0801 18.0933 16.616L12 20.134C11.0718 20.6699 9.9282 20.6699 9 20.134L2.90673 16.616C1.97853 16.0801 1.40673 15.0897 1.40673 14.0179V6.98205C1.40673 5.91025 1.97853 4.91987 2.90673 4.38397L9 0.866025Z"
        fill="currentColor"
        fillOpacity="0.25"
      />
      <path
        d="M9.25 1.29883C9.97506 0.880258 10.8577 0.854519 11.6025 1.2207L11.75 1.29883L17.8438 4.81738C18.6169 5.26404 19.0938 6.08946 19.0938 6.98242V14.0176C19.0938 14.9105 18.6169 15.736 17.8438 16.1826L11.75 19.7012C10.9765 20.1477 10.0235 20.1477 9.25 19.7012L3.15625 16.1826C2.38306 15.736 1.90625 14.9105 1.90625 14.0176V6.98242C1.90625 6.08946 2.38306 5.26404 3.15625 4.81738L9.25 1.29883Z"
        stroke="currentColor"
        strokeOpacity="0.9"
      />
      <path
        d="M14.875 6.84589V14.8751H14.0725V8.67065C14.0725 8.24573 13.6722 7.90123 13.1786 7.90123C12.6849 7.90123 12.2844 8.24573 12.2844 8.67065V14.8751H11.3663V9.40681C11.3663 8.98189 10.9663 8.63739 10.4724 8.63739C9.97852 8.63739 9.57848 8.98189 9.57848 9.40681V14.8751H8.66047V10.1432C8.66047 9.7183 8.26016 9.3738 7.76654 9.3738C7.27291 9.3738 6.8726 9.7183 6.8726 10.1432V14.8751H6.125V8.43039L14.8742 6.12512L14.875 6.84589Z"
        fill="currentColor"
      />
    </svg>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

const LINE = {
  wide: {
    vbw: 56,
    w: "w-[3.5em]",
    d: "M0 7.90152H24.0219L31.5075 0.415894L55.1162 0.415894",
  },
  narrow: {
    vbw: 48,
    w: "w-[3em]",
    d: "M0 7.90152H32.0219L39.5075 0.415894H47.1162",
  },
} as const;

function LabelLine({
  variant,
  className,
}: {
  variant: keyof typeof LINE;
  className?: string;
}) {
  const { vbw, w, d } = LINE[variant];
  return (
    <svg
      viewBox={`0 0 ${vbw} 9`}
      fill="none"
      aria-hidden
      className={cn("h-[0.5625em] shrink-0", w, className)}
    >
      <path opacity="0.2" d={d} stroke="white" strokeWidth="0.831737" />
    </svg>
  );
}

function PodiumLabel({ rank }: { rank: Rank }) {
  const { accent } = THEME[rank];
  if (rank === 1) {
    return (
      <div
        className="flex items-end justify-center gap-[0.5em]"
        style={{ color: accent }}
      >
        <LabelLine variant="narrow" />
        <div className="flex items-center gap-[0.25em] leading-none">
          <Laurel className="h-[1.5em] w-[1em] shrink-0" />
          <span className="text-[1.456em] font-medium tracking-[0.02em]">
            {LABELS[1]}
          </span>
          <Laurel className="h-[1.5em] w-[1em] shrink-0 -scale-x-100" />
        </div>
        <LabelLine variant="narrow" className="-scale-x-100" />
      </div>
    );
  }
  return (
    <div
      className="flex items-end justify-center gap-[0.5em] leading-none"
      style={{ color: accent }}
    >
      <LabelLine variant="wide" />
      <span className="text-[1.456em] font-medium tracking-[0.02em]">
        {LABELS[rank]}
      </span>
      <LabelLine variant="wide" className="-scale-x-100" />
    </div>
  );
}

const NOTCH_PATH =
  "M59.6445 0C92.5853 0 119.289 26.7038 119.289 59.6445C119.289 90.1612 96.3711 115.324 66.8076 118.862L59.6445 111.699L52.4805 118.862C22.9175 115.323 0 90.1607 0 59.6445C7.83417e-05 26.7038 26.7038 7.83519e-05 59.6445 0Z";

const GLOW: Record<Rank, string> = { 1: "#ffda74", 2: "#dbe5ec", 3: "#e3b991" };

function InnerCircle({ rank }: { rank: Rank }) {
  return (
    <svg
      viewBox="0 0 119.289 118.862"
      fill="none"
      aria-hidden
      className="absolute inset-0 h-full w-full overflow-visible"
    >
      <defs>
        <clipPath id={`ic-clip-${rank}`}>
          <path d={NOTCH_PATH} />
        </clipPath>
        <radialGradient id={`ic-glow-${rank}`} cx="0.5" cy="0.33" r="0.67">
          <stop offset="0.25" stopColor={GLOW[rank]} stopOpacity="0" />
          <stop offset="0.8" stopColor={GLOW[rank]} stopOpacity="0.16" />
          <stop offset="1" stopColor={GLOW[rank]} stopOpacity="0.45" />
        </radialGradient>
        <linearGradient
          id={`ic-stroke-${rank}`}
          x1="59.6445"
          y1="0"
          x2="59.6445"
          y2="118.862"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#DCDCD1" />
          <stop offset="0.0642444" stopColor="#90908C" />
          <stop offset="0.5" stopColor="#90908C" stopOpacity="0.4" />
          <stop offset="1" stopColor="#90908C" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      <path d={NOTCH_PATH} fill="white" fillOpacity="0.01" />
      <g clipPath={`url(#ic-clip-${rank})`}>
        <rect width="119.289" height="118.862" fill={`url(#ic-glow-${rank})`} />
      </g>
      <path
        d={NOTCH_PATH}
        fill="none"
        stroke={`url(#ic-stroke-${rank})`}
        strokeOpacity="0.2"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

const OUTER_PATH =
  "M68.0195 0C105.586 0 136.039 30.4535 136.039 68.0195C136.039 103.651 108.641 132.882 73.7627 135.798L68.0273 130.062L62.2891 135.8C27.4048 132.89 0 103.655 0 68.0195C0 30.4535 30.4535 0 68.0195 0Z";

const OUTER_STROKE: Record<Rank, Stop[]> = {
  1: [
    { o: 0, c: "#dcd79b" },
    { o: 0.0642, c: "#90825e" },
    { o: 0.5, c: "#949274", op: 0.4 },
    { o: 1, c: "#878267", op: 0.2 },
  ],
  2: [
    { o: 0, c: "#dcdcd1" },
    { o: 0.0642, c: "#90908c" },
    { o: 0.5, c: "#90908c", op: 0.4 },
    { o: 1, c: "#90908c", op: 0.2 },
  ],
  3: [
    { o: 0, c: "#debda4" },
    { o: 0.0642, c: "#8c7a6e" },
    { o: 0.5, c: "#8b7e6f", op: 0.4 },
    { o: 1, c: "#8b7d73", op: 0.2 },
  ],
};

function OuterRing({ rank }: { rank: Rank }) {
  return (
    <svg
      viewBox="0 0 136.039 135.8"
      fill="none"
      aria-hidden
      className="absolute inset-0 h-full w-full overflow-visible"
    >
      <defs>
        <linearGradient
          id={`or-stroke-${rank}`}
          x1="68.0195"
          y1="0"
          x2="68.0195"
          y2="136.038"
          gradientUnits="userSpaceOnUse"
        >
          {OUTER_STROKE[rank].map((s) => (
            <stop
              key={s.o}
              offset={s.o}
              stopColor={s.c}
              stopOpacity={s.op ?? 1}
            />
          ))}
        </linearGradient>
      </defs>
      <path
        d={OUTER_PATH}
        fill="none"
        stroke={`url(#or-stroke-${rank})`}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function Avatar({
  rank,
  name,
  big,
}: {
  rank: Rank;
  name: string;
  big?: boolean;
}) {
  const { accent } = THEME[rank];
  return (
    <div
      className={cn(
        "relative",
        big ? "w-[8.5em] h-[8.5em]" : "w-[5.9981em] h-[5.9981em]",
      )}
    >
      <OuterRing rank={rank} />
      <div className="absolute top-[6.155%] left-[6.155%] h-[87.69%] w-[87.69%]">
        <InnerCircle rank={rank} />
      </div>
      <span
        className={cn(
          "absolute inset-0 flex items-center justify-center font-medium leading-none",
          big ? "text-[2em]" : "text-[1.664em]",
        )}
        style={{ color: accent }}
      >
        {initials(name)}
      </span>
    </div>
  );
}

function StatRow({
  entry,
  big,
  className,
}: {
  entry: LeaderboardEntry | null;
  big?: boolean;
  className?: string;
}) {
  const stats = entry
    ? [
        { label: "Trades", value: entry.trades.toLocaleString("en-US") },
        { label: "Winrate %", value: `${entry.winrate.toFixed(1)}%` },
        { label: "PF", value: entry.profitFactor.toFixed(2) },
      ]
    : [
        { label: "Trades", value: "—" },
        { label: "Winrate %", value: "—" },
        { label: "PF", value: "—" },
      ];
  return (
    <div
      className={cn(
        "flex items-start justify-between",
        big ? "w-[14.1875em]" : "w-[13.5em]",
        className,
      )}
    >
      {stats.map((s) => (
        <div key={s.label} className="flex flex-col items-center gap-[0.25em]">
          <span className="font-jetbrainsmono text-[0.875em] whitespace-nowrap text-white/40 uppercase">
            {s.label}
          </span>
          <span className="rounded-[0.416em] bg-white/5 px-[0.416em] py-[0.3125em] text-[1em] font-medium leading-none text-white/80 tabular-nums">
            {s.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function ShieldContent({
  rank,
  entry,
  big,
}: {
  rank: Rank;
  entry: LeaderboardEntry | null;
  big?: boolean;
}) {
  const empty = !entry;
  return (
    <div
      className={cn(
        "absolute inset-0 flex flex-col items-center pb-[40%]",
        big ? "pt-[18%]" : "pt-[10%]",
      )}
    >
      <Avatar rank={rank} name={entry?.name ?? ""} big={big} />
      <div
        className={cn(
          "mt-auto flex flex-col items-center",
          empty && "opacity-40",
        )}
      >
        {big && (
          <div
            className="flex items-center gap-[0.5em]"
            style={{ color: GLOW[rank] }}
          >
            <PointsIcon className="h-[1.3125em] w-[1.3125em] shrink-0" />
            <span className="text-[1.75em] font-medium tracking-[0.033em] tabular-nums">
              {entry ? entry.points.toLocaleString("en-US") : "—"}
            </span>
          </div>
        )}
        <span
          className={cn(
            "text-[1em]",
            empty ? "text-white/60" : "text-white",
            big ? "mt-[0.75em]" : "mt-[0.5em]",
          )}
        >
          {entry?.country && (
            <CountryFlag
              country={entry.country}
              className="mr-[0.35em] inline-block h-[0.75em] w-[1em] rounded-[0.15em] align-[-0.12em]"
            />
          )}
          {entry ? entry.name : "Unranked"}
        </span>
        <StatRow
          entry={entry}
          big={big}
          className={big ? "mt-[1em]" : "mt-[0.75em]"}
        />
      </div>
    </div>
  );
}

function ShieldClips() {
  return (
    <svg aria-hidden className="pointer-events-none absolute h-0 w-0">
      <defs>
        {(Object.keys(SHAPES) as (keyof typeof SHAPES)[]).map((k) => (
          <clipPath
            key={k}
            id={`podium-clip-${k}`}
            clipPathUnits="objectBoundingBox"
          >
            <path d={SHAPES[k].clip} />
          </clipPath>
        ))}
      </defs>
    </svg>
  );
}

function Shield({
  rank,
  className,
  children,
}: {
  rank: Rank;
  className?: string;
  children?: ReactNode;
}) {
  const { accent, shape, stroke } = THEME[rank];
  const { h, border: path, wrap } = SHAPES[shape];

  return (
    <div
      className={cn("relative w-full", className)}
      style={{ aspectRatio: `267 / ${h}` }}
    >
      <div
        className="absolute inset-0 backdrop-blur-[0.4156em]"
        style={{
          clipPath: `url(#podium-clip-${shape})`,
          background: `linear-gradient(180deg, color-mix(in srgb, ${accent} 15%, transparent) 0%, transparent 100%), rgba(255, 255, 255, 0.06)`,
        }}
      />
      <svg
        aria-hidden
        viewBox={`0 0 267 ${h}`}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full overflow-visible"
      >
        <defs>
          <linearGradient
            id={`podium-stroke-${rank}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            {stroke.map((s) => (
              <stop
                key={s.o}
                offset={s.o}
                stopColor={s.c}
                stopOpacity={s.op ?? 1}
              />
            ))}
          </linearGradient>
        </defs>
        <path
          d={path}
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={path}
          fill="none"
          stroke={`url(#podium-stroke-${rank})`}
          strokeWidth={2.5}
        />
        <path
          d={wrap}
          transform="translate(-12 -4)"
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={wrap}
          transform="translate(280 -4) scale(-1 1)"
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {children}
    </div>
  );
}

function PodiumColumn({
  rank,
  entry,
  drop,
  onClick,
}: {
  rank: Rank;
  entry: LeaderboardEntry | null;
  drop?: boolean;
  onClick: () => void;
}) {
  return (
    <CarouselItem
      onClick={onClick}
      tabIndex={0}
      aria-label={`${LABELS[rank]} place`}
      className="[container-type:inline-size] flex flex-col items-center pl-0 lg:flex-1 max-lg:basis-[36.045rem] max-lg:cursor-pointer"
    >
      <FadeIn delay={rank === 1 ? 0.24 : rank === 2 ? 0.06 : 0.15} className="w-full">
        <div
          className={cn(
            "flex w-full flex-col items-center gap-[0.75em] text-[5.549cqw]",
            drop && "lg:pt-[1.5em]",
          )}
        >
          <PodiumLabel rank={rank} />
          <Shield rank={rank} className="max-lg:w-[92.4%]">
            <ShieldContent rank={rank} entry={entry} big={rank === 1} />
          </Shield>
        </div>
      </FadeIn>
    </CarouselItem>
  );
}

export function Podium({ top }: { top: LeaderboardEntry[] }) {
  const [api, setApi] = useState<CarouselApi>();
  const first = top[0] ?? null;
  const second = top[1] ?? null;
  const third = top[2] ?? null;

  return (
    <div className="relative mx-auto w-full max-w-[124.795rem] max-lg:-mx-[2rem] max-lg:w-[calc(100%+4rem)] max-lg:max-w-none">
      <ShieldClips />
      <Carousel
        setApi={setApi}
        opts={{
          align: "center",
          startIndex: 1,
          containScroll: false,
          breakpoints: { "(min-width: 993px)": { active: false } },
        }}
        className="max-lg:-mb-[24px] lg:[&>div]:overflow-visible"
      >
        <CarouselContent className="items-start max-lg:gap-[1.5rem] max-lg:pb-[24px] lg:justify-center lg:gap-[8.33rem]">
          <PodiumColumn rank={2} entry={second} drop onClick={() => api?.scrollTo(0)} />
          <PodiumColumn rank={1} entry={first} onClick={() => api?.scrollTo(1)} />
          <PodiumColumn rank={3} entry={third} drop onClick={() => api?.scrollTo(2)} />
        </CarouselContent>
      </Carousel>
    </div>
  );
}
